import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { MessageCircle, X, Send, Bot, User, Loader2, Sparkles, MapPin, BookOpen, Truck, Navigation, Car, Bike, Bus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useLocation, useNavigate } from "react-router-dom";

interface Message {
  role: "user" | "assistant";
  content: string;
}

// Category selection for different license types
interface CategoryOption {
  id: string;
  label: string;
  emoji: string;
  searchType: string;
  icon: React.ReactNode;
}

const categoryOptions: CategoryOption[] = [
  { id: "auto", label: "Auto", emoji: "🚗", searchType: "rijschool", icon: <Car className="h-5 w-5" /> },
  { id: "brommer", label: "Brommer", emoji: "🛵", searchType: "tweewieler-am", icon: <Bike className="h-5 w-5" /> },
  { id: "motor", label: "Motor", emoji: "🏍️", searchType: "tweewieler-a", icon: <Bike className="h-5 w-5" /> },
  { id: "vrachtwagen", label: "Vrachtwagen", emoji: "🚛", searchType: "code95", icon: <Truck className="h-5 w-5" /> },
  { id: "bus", label: "Bus", emoji: "🚌", searchType: "code95-bus", icon: <Bus className="h-5 w-5" /> },
];

// Parse markdown-style links and bold text into React elements
function parseMarkdown(text: string, onNavigate?: (url: string) => void): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let keyIndex = 0;
  let remaining = text;
  
  // Process text character by character to handle all markdown patterns
  while (remaining.length > 0) {
    // Look for markdown link pattern [text](url)
    const linkMatch = remaining.match(/^\[([^\[\]]+)\]\(([^()]+)\)/);
    if (linkMatch) {
      const linkText = linkMatch[1];
      const url = linkMatch[2];
      
      if (url.startsWith("/")) {
        // Internal link - make it clickable
        parts.push(
          <span
            key={`link-${keyIndex++}`}
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (onNavigate) {
                onNavigate(url);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (onNavigate) {
                  onNavigate(url);
                }
              }
            }}
            className="font-semibold text-primary underline underline-offset-2 hover:text-primary/80 active:text-primary/60 cursor-pointer inline select-none px-1 py-0.5 -mx-1 rounded touch-manipulation"
            style={{ pointerEvents: 'auto', WebkitTapHighlightColor: 'rgba(0,0,0,0.1)' }}
          >
            {linkText}
          </span>
        );
      } else {
        // External link
        parts.push(
          <a
            key={`link-${keyIndex++}`}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-primary underline underline-offset-2 hover:text-primary/80"
            onClick={(e) => e.stopPropagation()}
          >
            {linkText}
          </a>
        );
      }
      remaining = remaining.slice(linkMatch[0].length);
      continue;
    }
    
    // Look for bold pattern **text**
    const boldMatch = remaining.match(/^\*\*([^*]+)\*\*/);
    if (boldMatch) {
      parts.push(
        <strong key={`bold-${keyIndex++}`} className="font-semibold">
          {boldMatch[1]}
        </strong>
      );
      remaining = remaining.slice(boldMatch[0].length);
      continue;
    }
    
    // Look for single asterisk bold *text*
    const italicMatch = remaining.match(/^\*([^*]+)\*/);
    if (italicMatch) {
      parts.push(
        <strong key={`italic-${keyIndex++}`} className="font-semibold">
          {italicMatch[1]}
        </strong>
      );
      remaining = remaining.slice(italicMatch[0].length);
      continue;
    }
    
    // Find next special character or end of string
    const nextSpecial = remaining.search(/[\[\*]/);
    if (nextSpecial === -1) {
      // No more special characters, add rest of text
      parts.push(remaining);
      break;
    } else if (nextSpecial === 0) {
      // Special character at start but didn't match pattern, add just this char
      parts.push(remaining[0]);
      remaining = remaining.slice(1);
    } else {
      // Add text up to the special character
      parts.push(remaining.slice(0, nextSpecial));
      remaining = remaining.slice(nextSpecial);
    }
  }
  
  return parts.length > 0 ? parts : [text];
}

// Component to render message content with parsed markdown
function MessageContent({ content, onNavigate }: { content: string; onNavigate?: (url: string) => void }) {
  const lines = content.split("\n");
  
  return (
    <>
      {lines.map((line, lineIndex) => (
        <span key={lineIndex}>
          {parseMarkdown(line, onNavigate)}
          {lineIndex < lines.length - 1 && <br />}
        </span>
      ))}
    </>
  );
}

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-assistant`;

type AIAssistantMode = "floating" | "embedded";

interface AIAssistantProps {
  /** Default: floating */
  mode?: AIAssistantMode;
  /** Only used for embedded mode; opens chat by default */
  defaultOpen?: boolean;
}

// Main category buttons shown initially
interface MainCategory {
  id: string;
  label: string;
  emoji: string;
  action: string;
}

const mainCategories: MainCategory[] = [
  { id: "opleider", label: "Opleider", emoji: "🚗", action: "select-license-type" },
  { id: "theorie", label: "Theorie", emoji: "📚", action: "theorie" },
  { id: "cbr", label: "CBR", emoji: "🎯", action: "cbr" },
  { id: "zakelijk", label: "Zakelijk", emoji: "💼", action: "zakelijk" },
];

export function AIAssistant({ mode = "floating", defaultOpen = false }: AIAssistantProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(mode === "embedded" ? defaultOpen : false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(true);
  const [showCategorySelection, setShowCategorySelection] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<CategoryOption | null>(null);
  const [userLocation, setUserLocation] = useState<{ city: string; lat: number; lon: number } | null>(null);
  const [isLocating, setIsLocating] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Detect current page context
  const pageContext = useMemo(() => {
    const path = location.pathname;
    
    // Check if on a specific rijschool page
    if (path.startsWith("/rijschool/")) {
      const slug = path.replace("/rijschool/", "");
      // Convert slug to readable name
      const name = slug
        .split("-")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
      return { type: "rijschool", slug, name };
    }
    
    if (path === "/rijscholen" || path === "/rijscholen/kaart") {
      return { type: "rijscholen-lijst" };
    }
    
    if (path === "/online-theorie") {
      return { type: "online-theorie" };
    }
    
    if (path === "/theorie-boeken" || path === "/boeken") {
      return { type: "theorie-boeken" };
    }
    
    if (path.startsWith("/code95")) {
      return { type: "code95" };
    }
    
    if (path.startsWith("/taxi")) {
      return { type: "taxi" };
    }
    
    if (path.startsWith("/tweewielers")) {
      return { type: "tweewielers" };
    }
    
    return { type: "home" };
  }, [location.pathname]);

  // GPS location detection
  const detectLocation = useCallback(async () => {
    if (!navigator.geolocation) {
      return;
    }
    
    setIsLocating(true);
    
    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 300000, // 5 minutes cache
        });
      });
      
      const { latitude, longitude } = position.coords;
      
      // Reverse geocode to get city name using Nominatim
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&accept-language=nl`
      );
      
      if (response.ok) {
        const data = await response.json();
        const city = data.address?.city || data.address?.town || data.address?.village || data.address?.municipality;
        
        if (city) {
          setUserLocation({ city, lat: latitude, lon: longitude });
          return city;
        }
      }
    } catch (error) {
      console.log("Location detection failed:", error);
    } finally {
      setIsLocating(false);
    }
    
    return null;
  }, []);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      const scrollElement = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollElement) {
        scrollElement.scrollTop = scrollElement.scrollHeight;
      }
    }
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const sendMessage = useCallback(async (messageText?: string) => {
    const text = messageText || input.trim();
    if (!text || isLoading) return;

    const userMessage: Message = { role: "user", content: text };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);
    setShowQuickActions(false);

    let assistantContent = "";
    const updateAssistant = (chunk: string) => {
      assistantContent += chunk;
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === "assistant") {
          return prev.map((m, i) => 
            i === prev.length - 1 ? { ...m, content: assistantContent } : m
          );
        }
        return [...prev, { role: "assistant", content: assistantContent }];
      });
    };

    try {
      const resp = await fetch(CHAT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({
          messages: [...messages, userMessage],
          userLocation: userLocation?.city,
          pageContext,
        }),
      });

      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        throw new Error(errorData.error || "Er ging iets mis");
      }

      if (!resp.body) throw new Error("Geen response body");

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = buffer.indexOf("\n")) !== -1) {
          let line = buffer.slice(0, newlineIndex);
          buffer = buffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") break;

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) updateAssistant(content);
          } catch {
            buffer = line + "\n" + buffer;
            break;
          }
        }
      }
    } catch (error) {
      console.error("Chat error:", error);
      const errorMessage = error instanceof Error ? error.message : "Er ging iets mis";
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `Oeps! ${errorMessage} 😔\n\nProbeer het later opnieuw of bekijk onze website voor meer informatie.`,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, messages]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Handle main category selection
  const handleMainCategorySelect = (category: MainCategory) => {
    setShowQuickActions(false);
    
    // Add user's choice as a message
    setMessages((prev) => [
      ...prev,
      { role: "user", content: `${category.emoji} ${category.label}` },
    ]);
    
    if (category.action === "select-license-type") {
      // Show license type selection for opleiders
      setShowCategorySelection(true);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `Top! 🎯 Welk type rijbewijs wil je halen?\n\nKies hieronder een categorie:`,
        },
      ]);
    } else if (category.action === "theorie") {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `Goed bezig! 📚 Wat zoek je precies?\n\n**Online theorie cursus**\nLeer thuis op je eigen tempo\n\n**Theorie boeken**\nFysieke lesboeken bestellen\n\nKlik hieronder om te bekijken:\n\n👉 [Online cursussen bekijken](/online-theorie)\n\n👉 [Theorie boeken bekijken](/theorie-boeken)\n\nOf vertel me meer over wat je zoekt!`,
        },
      ]);
    } else if (category.action === "cbr") {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `Prima! 🎯 Wat wil je weten over het CBR?\n\nIk kan je helpen met:\n• Informatie over theorie-examens\n• Praktijkexamen tips\n• CBR locaties en wachttijden\n• Faalangst cursussen\n\n👉 [Bekijk CBR informatie](/cbr)\n\nOf stel je vraag!`,
        },
      ]);
    } else if (category.action === "zakelijk") {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `Zakelijk! 💼 Wat voor opleiding zoek je?\n\n• **Code 95** - Nascholing voor vracht-/buschauffeurs\n• **Taxi** - Chauffeurspas opleiding\n• **Rijinstructeur** - Zelf instructeur worden\n\n👉 [Code 95 opleiders](/code95)\n👉 [Taxi opleiders](/taxi)\n👉 [Instructeur opleiders](/vacatures/opleiders-instructeur-diploma)\n\nOf vertel me meer over wat je zoekt!`,
        },
      ]);
    }
  };

  // Handle category selection
  const handleCategorySelect = async (category: CategoryOption) => {
    setSelectedCategory(category);
    setShowCategorySelection(false);
    
    // Add user's choice as a message
    setMessages((prev) => [
      ...prev,
      { role: "user", content: `${category.emoji} ${category.label}` },
    ]);
    
    // Ask for location
    setMessages((prev) => [
      ...prev,
      {
        role: "assistant",
        content: `Top keuze! ${category.emoji}\n\nIn welke **stad of regio** zoek je een ${category.label.toLowerCase()} opleider?\n\nOf klik op 📍 hieronder om je locatie automatisch te detecteren.`,
      },
    ]);
  };

  // Handle location for category search - navigates to the correct page
  const handleLocationForCategory = useCallback(async (locationCity: string) => {
    if (!selectedCategory) return;
    
    let searchMessage = "";
    let targetUrl = "";
    
    switch (selectedCategory.searchType) {
      case "rijschool":
        searchMessage = `Ik zoek een autorijschool in ${locationCity}`;
        targetUrl = `/rijscholen?cbr_gebied=${encodeURIComponent(locationCity)}`;
        break;
      case "tweewieler-am":
        searchMessage = `Ik zoek een brommer opleiding in ${locationCity}`;
        targetUrl = `/tweewielers?locatie=${encodeURIComponent(locationCity)}`;
        break;
      case "tweewieler-a":
        searchMessage = `Ik zoek een motor opleiding in ${locationCity}`;
        targetUrl = `/tweewielers?locatie=${encodeURIComponent(locationCity)}`;
        break;
      case "code95":
        searchMessage = `Ik zoek Code 95 nascholing voor vrachtwagen in ${locationCity}`;
        targetUrl = `/code95?locatie=${encodeURIComponent(locationCity)}`;
        break;
      case "code95-bus":
        searchMessage = `Ik zoek Code 95 nascholing voor bus in ${locationCity}`;
        targetUrl = `/code95?locatie=${encodeURIComponent(locationCity)}`;
        break;
      default:
        searchMessage = `Ik zoek een opleider in ${locationCity}`;
        targetUrl = `/rijscholen?cbr_gebied=${encodeURIComponent(locationCity)}`;
    }
    
    // Navigate to the correct page immediately
    if (targetUrl && location.pathname !== targetUrl.split('?')[0]) {
      navigate(targetUrl);
    }
    
    // Send the search message to get AI response with 3 schools
    sendMessage(searchMessage);
    setSelectedCategory(null);
  }, [selectedCategory, sendMessage, navigate, location.pathname]);

  // Context-aware initial greeting when chat opens
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      let greeting = "";
      
      // Context-specific greetings
      if (pageContext.type === "rijschool" && pageContext.name) {
        greeting = `Hallo! 👋 Ik ben Ricky, jouw persoonlijke assistent!

Ik zie dat je naar **${pageContext.name}** kijkt. 🚗

Kan ik je helpen met:
• Meer informatie over deze rijschool?
• Vergelijken met andere rijscholen in de buurt?
• Tips voor het kiezen van een rijschool?

Wat wil je weten?`;
      } else if (pageContext.type === "rijscholen-lijst") {
        greeting = `Hallo! 👋 Ik ben Ricky!

Ik zie dat je rijscholen aan het vergelijken bent. Slim! 💪

Vertel me je **stad of regio** en ik zoek de beste rijscholen met de hoogste slagingspercentages voor je uit. 

Of klik op 📍 hieronder om je locatie te delen!`;
      } else if (pageContext.type === "online-theorie") {
        greeting = `Hallo! 👋 Ik ben Ricky!

Je bent op de juiste plek voor online theorie cursussen! 📚

Heb je vragen over:
• Welke cursus het beste bij jou past?
• Wat je nodig hebt voor je CBR theorie-examen?
• Hoe lang je moet studeren?

Vraag maar raak!`;
      } else if (pageContext.type === "code95") {
        greeting = `Hallo! 👋 Ik ben Ricky!

Op zoek naar Code 95 nascholing? 🚛

Vertel me in welke regio je zoekt en ik help je de beste erkende opleiders te vinden!`;
      } else {
        // Default greeting - simplified for button selection with login encouragement
        greeting = `Hallo! 👋 Ik ben Ricky, jouw persoonlijke assistent!

Kies hieronder wat je zoekt:

💡 **Tip:** [Maak gratis een account aan](/auth) voor extra voordelen:
• Kolb leerstijltest
• Oefenquiz vragen
• Persoonlijke rijschool matches`;
      }
      
      setMessages([{ role: "assistant", content: greeting }]);
    }
  }, [isOpen, messages.length, pageContext]);

  // Reset chat
  const resetChat = () => {
    setMessages([]);
    setShowQuickActions(true);
    setShowCategorySelection(false);
    setSelectedCategory(null);
  };

  // Handle link clicks in chat messages - navigate and optionally close chat
  const handleChatLinkClick = useCallback((url: string) => {
    navigate(url);
    // Don't close the chat so user can see the results
  }, [navigate]);

  const portalTarget = typeof document !== "undefined" ? document.body : null;

  const floatingFab = (
    <button
      id="ricky-fab"
      onClick={() => setIsOpen(true)}
      style={{
        // Inline fallback for Android/Samsung browsers: keep it simple (no env()).
        // src/index.css still enforces the preferred positioning with safe-area when supported.
        position: "fixed",
        right: "1rem",
        bottom: "calc(var(--mobile-bottom-nav-height, 72px) + 12px)",
        zIndex: 100000,
        opacity: 1,
        visibility: "visible",
        transform: "translate3d(0, 0, 0)", // Force GPU layer for iOS Safari
        WebkitTransform: "translate3d(0, 0, 0)",
        WebkitBackfaceVisibility: "hidden",
        backfaceVisibility: "hidden",
        willChange: "transform",
        display: isOpen ? "none" : "flex",
      }}
      className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-all hover:scale-110 hover:shadow-xl pointer-events-auto touch-manipulation"
      aria-label="Open AI Assistent"
    >
      <MessageCircle className="h-6 w-6" />
      <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-orange-500 text-[10px] font-bold text-white">
        AI
      </span>
    </button>
  );

  const chatWindow = (
    <div
      style={
        mode === "embedded"
          ? undefined
          : {
              position: "fixed",
              bottom: 0,
              right: 0,
              zIndex: 100000,
              transform: "translate3d(0, 0, 0)", // GPU acceleration for mobile browsers
              WebkitTransform: "translate3d(0, 0, 0)",
              WebkitBackfaceVisibility: "hidden",
              backfaceVisibility: "hidden",
            }
      }
      className={cn(
        "flex flex-col bg-background shadow-2xl",
        mode === "embedded"
          ? "h-[100dvh] w-full"
          : "h-[100dvh] w-full md:bottom-6 md:right-4 md:h-[600px] md:w-[420px] md:rounded-2xl md:border"
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b bg-gradient-to-r from-primary to-primary/80 px-4 py-3 text-primary-foreground md:rounded-t-2xl">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20">
              <Bot className="h-5 w-5" />
            </div>
            <span className="absolute -bottom-0.5 -right-0.5 flex h-3 w-3 items-center justify-center rounded-full bg-green-400 ring-2 ring-primary" />
          </div>
          <div>
            <span className="font-semibold">Ricky</span>
            <p className="text-xs text-primary-foreground/80">AI Assistent • Online</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={resetChat}
            className="h-8 px-2 text-xs text-primary-foreground hover:bg-primary-foreground/20"
          >
            Nieuw gesprek
          </Button>
          {mode === "floating" && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(false)}
              className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20"
            >
              <X className="h-5 w-5" />
            </Button>
          )}
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4" ref={scrollRef}>
        <div className="flex flex-col gap-4">
          {messages.map((msg, i) => (
            <div key={i} className={cn("flex gap-2", msg.role === "user" ? "flex-row-reverse" : "flex-row")}>
              <div
                className={cn(
                  "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
                  msg.role === "user" ? "bg-primary text-primary-foreground" : "bg-gradient-to-br from-primary/20 to-orange-100"
                )}
              >
                {msg.role === "user" ? <User className="h-4 w-4" /> : <Sparkles className="h-4 w-4 text-primary" />}
              </div>
              <div
                className={cn(
                  "max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed",
                  msg.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted"
                )}
              >
                <div className="whitespace-pre-wrap [&_span[role=button]]:pointer-events-auto [&_a]:pointer-events-auto">
                  <MessageContent content={msg.content} onNavigate={handleChatLinkClick} />
                </div>
              </div>
            </div>
          ))}

          {/* Loading indicator */}
          {isLoading && messages[messages.length - 1]?.role === "user" && (
            <div className="flex gap-2">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-orange-100">
                <Sparkles className="h-4 w-4 text-primary" />
              </div>
              <div className="flex items-center gap-2 rounded-2xl bg-muted px-4 py-3">
                <div className="flex gap-1">
                  <span className="h-2 w-2 animate-bounce rounded-full bg-primary/60" style={{ animationDelay: "0ms" }} />
                  <span className="h-2 w-2 animate-bounce rounded-full bg-primary/60" style={{ animationDelay: "150ms" }} />
                  <span className="h-2 w-2 animate-bounce rounded-full bg-primary/60" style={{ animationDelay: "300ms" }} />
                </div>
                <span className="text-sm text-muted-foreground">Ricky is aan het typen...</span>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Category Selection */}
      {showCategorySelection && (
        <div className="border-t bg-muted/30 px-4 py-3">
          <p className="mb-3 text-sm font-medium text-foreground">Kies je rijbewijstype:</p>
          <div className="grid grid-cols-2 gap-2">
            {categoryOptions.map((category) => (
              <button
                key={category.id}
                onClick={() => handleCategorySelect(category)}
                className="flex items-center gap-2 rounded-xl border-2 bg-background px-3 py-4 text-sm font-medium transition-all hover:border-primary hover:bg-primary/5 active:bg-primary/10 active:scale-[0.98] touch-manipulation"
                style={{ WebkitTapHighlightColor: 'rgba(0,0,0,0.1)' }}
              >
                <span className="text-xl">{category.emoji}</span>
                <span>{category.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Main Category Selection - shown initially */}
      {showQuickActions && messages.length === 1 && !showCategorySelection && (
        <div className="border-t bg-muted/30 px-4 py-3">
          <p className="mb-2 text-xs font-medium text-muted-foreground">Waar kan ik je mee helpen?</p>
          <div className="grid grid-cols-2 gap-2">
            {mainCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => handleMainCategorySelect(category)}
                className="flex items-center justify-center gap-2 rounded-xl border-2 bg-background px-3 py-4 text-sm font-medium transition-all hover:border-primary hover:bg-primary/5 active:bg-primary/10 active:scale-[0.98] touch-manipulation"
                style={{ WebkitTapHighlightColor: 'rgba(0,0,0,0.1)' }}
              >
                <span className="text-lg">{category.emoji}</span>
                <span>{category.label}</span>
              </button>
            ))}
          </div>
          <p className="mt-2 text-center text-[10px] text-muted-foreground">Of typ hieronder je vraag</p>
        </div>
      )}

      {/* Input */}
      <div className="border-t bg-background p-4">
        <div className="flex gap-2">
          {/* GPS Location Button */}
          <Button
            onClick={async () => {
              const city = await detectLocation();
              if (city) {
                if (selectedCategory) {
                  // Use the category search flow
                  handleLocationForCategory(city);
                } else {
                  // Trigger category selection first
                  setShowCategorySelection(true);
                  setShowQuickActions(false);
                  setUserLocation({ city, lat: 0, lon: 0 }); // Store for later
                  setMessages((prev) => [
                    ...prev,
                    {
                      role: "assistant",
                      content: `📍 Ik zie dat je in **${city}** bent!\n\nWelk type rijbewijs wil je halen? Kies hieronder:`,
                    },
                  ]);
                }
              }
            }}
            disabled={isLoading || isLocating}
            variant="outline"
            size="icon"
            className={cn("h-10 w-10 shrink-0 rounded-full", userLocation && "bg-primary/10 border-primary text-primary")}
            title={userLocation ? `Locatie: ${userLocation.city}` : "Detecteer mijn locatie"}
          >
            {isLocating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Navigation className={cn("h-4 w-4", userLocation && "fill-primary")} />
            )}
          </Button>

          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                if (selectedCategory && input.trim()) {
                  // User typed a location after selecting category
                  handleLocationForCategory(input.trim());
                  setInput("");
                } else {
                  sendMessage();
                }
              }
            }}
            placeholder={
              selectedCategory
                ? `Typ je stad voor ${selectedCategory.label.toLowerCase()}...`
                : userLocation
                  ? `Vraag over ${userLocation.city}...`
                  : "Typ je vraag..."
            }
            className="flex-1 rounded-full border bg-muted px-4 py-2.5 text-sm outline-none transition-all focus:ring-2 focus:ring-primary"
            disabled={isLoading}
          />
          <Button
            onClick={() => {
              if (selectedCategory && input.trim()) {
                handleLocationForCategory(input.trim());
                setInput("");
              } else {
                sendMessage();
              }
            }}
            disabled={!input.trim() || isLoading}
            size="icon"
            className="h-10 w-10 shrink-0 rounded-full"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
        {userLocation && <p className="mt-1 text-center text-[10px] text-primary">📍 {userLocation.city} gedetecteerd</p>}
        <p className="mt-1 text-center text-[10px] text-muted-foreground">✨ Powered by AI • Theorieaanbieders.nl</p>
      </div>
    </div>
  );

  const ui = (
    <>
      {mode === "floating" && floatingFab}
      {(mode === "embedded" || isOpen) && chatWindow}
    </>
  );

  if (mode === "embedded") return ui;

  // IMPORTANT: Render via portal so `position: fixed` is always relative to the viewport
  // (some mobile browsers treat fixed elements inside transformed ancestors incorrectly).
  return portalTarget ? createPortal(ui, portalTarget) : ui;
}
